import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";

const CELL_COLOR = {
  1: "bg-rose-500", 2: "bg-amber-400", 3: "bg-emerald-500", 4: "bg-sky-600", null: "bg-slate-200 dark:bg-slate-700",
};

export default function Heatmap() {
  const [campagnes, setCampagnes] = useState([]);
  const [campagneId, setCampagneId] = useState("");
  const [data, setData] = useState(null);
  const [cell, setCell] = useState(null);
  const [transversal, setTransversal] = useState(null);

  useEffect(() => {
    api.get("/campagnes").then((res) => {
      setCampagnes(res.data);
      const cross = res.data.find((c) => c.type_campagne === "CROSS_ASSESSMENT");
      if (cross) setCampagneId(cross.campagne_id);
    });
  }, []);

  useEffect(() => {
    if (campagneId) api.get("/heatmap", { params: { campagne_id: campagneId } }).then((res) => setData(res.data));
  }, [campagneId]);

  const openCell = (row, siteCell) => {
    setCell({ row, siteCell });
  };

  const openTransversal = (row) => {
    api.get(`/heatmap/transversal/${row.requirement_id}`, { params: { campagne_id: campagneId } }).then((res) => setTransversal(res.data));
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="heatmap-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Heatmap Nationale</h1>
        <Select value={campagneId} onValueChange={setCampagneId}>
          <SelectTrigger className="w-56" data-testid="heatmap-campagne-select"><SelectValue placeholder="Campagne" /></SelectTrigger>
          <SelectContent>{campagnes.map((c) => <SelectItem key={c.campagne_id} value={c.campagne_id}>{c.nom}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="flex gap-3 text-xs items-center flex-wrap">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-500 inline-block" /> Niveau 1</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-400 inline-block" /> Niveau 2</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-500 inline-block" /> Niveau 3</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-sky-600 inline-block" /> Niveau 4</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-200 inline-block" /> Non évalué</span>
      </div>

      {data && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="text-xs w-full">
            <thead>
              <tr>
                <th className="sticky left-0 bg-background p-2 text-left border-b border-r min-w-[220px]">Exigence</th>
                {data.sites.map((s) => <th key={s.site_id} className="p-2 border-b whitespace-nowrap min-w-[70px]">{s.nom_site}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.matrix.map((row) => (
                <tr key={row.requirement_id}>
                  <td className="sticky left-0 bg-background p-2 border-b border-r cursor-pointer hover:underline" onClick={() => openTransversal(row)}>
                    <span className="font-mono">{row.reference}</span> — {row.question_fr.slice(0, 40)}...
                  </td>
                  {row.cells.map((c) => (
                    <td key={c.site_id} className="p-1 border-b text-center">
                      <button
                        data-testid="heatmap-cell"
                        className={`h-7 w-7 rounded ${CELL_COLOR[c.score] || CELL_COLOR[null]} hover:ring-2 hover:ring-offset-1 hover:ring-slate-400`}
                        onClick={() => openCell(row, c)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!cell} onOpenChange={() => setCell(null)}>
        <DialogContent data-testid="heatmap-cell-modal">
          <DialogHeader><DialogTitle>Détail de la cellule</DialogTitle></DialogHeader>
          {cell && (
            <div className="text-sm space-y-1">
              <div><b>Site:</b> {cell.siteCell.site_nom}</div>
              <div><b>Référence:</b> {cell.row.reference}</div>
              <div><b>Question:</b> {cell.row.question_fr}</div>
              <div><b>Score:</b> {cell.siteCell.score ?? "Non évalué"}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!transversal} onOpenChange={() => setTransversal(null)}>
        <DialogContent data-testid="heatmap-transversal-modal">
          <DialogHeader><DialogTitle>Analyse transversale</DialogTitle></DialogHeader>
          {transversal && (
            <div className="text-sm space-y-2">
              <p className="font-medium">{transversal.requirement.question_fr}</p>
              <div>Sites évalués: {transversal.nb_sites_evalues}</div>
              <div className="flex gap-2">
                {Object.entries(transversal.distribution).map(([lvl, count]) => (
                  <Badge key={lvl} variant="outline">Niveau {lvl}: {count}</Badge>
                ))}
              </div>
              <div>Sites insuffisants: {transversal.sites_insuffisants.join(", ") || "Aucun"}</div>
              <div>Actions: {transversal.nb_actions} ({transversal.nb_actions_echues} échues)</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
